import AccordionSection from 'common/AccordionSection'
import LoadingSpinner from 'components/LoadingSpinner'
import SfCheckbox from 'components/overrides/SfCheckbox'
import { getEstimate, postEstimate } from 'modules/aobPayments/fetchPromises'
import React, { useState } from 'react'
import { useSnlInvoiceStore } from 'stores/SNLInvoiceStore'
import { reportToSentry } from 'utils/reportToSentry'

interface OrderEditData {
  deductible: number;
  deductible_remaining: number;
  monthly_max: number;
  total_charge: number;
  coinsurance_percentage: number;
  number_of_months: number;
  est_monthly_due: number;
  total_to_collect: number;
}

function EditInvoice() {
  const [ isEditing, setIsEditing ] = useState<boolean>( false )
  const [ keepOpen, setKeepOpen ] = useState<boolean>( true )
  const [ editOrderError, setEditOrderError ] = useState<string>( `` )
  const [ loading, setLoading ] = useState<boolean>( false )
  const { snlInvoice, editedInvoice, snlInvoiceHash, error, setEditedInvoice, removedItems, setRemovedItems } = useSnlInvoiceStore()

  const invoice = !isEditing && editedInvoice ? editedInvoice : snlInvoice

  const removeItem = ( dmeId: number ) => {
    const itemInRemoved = removedItems?.findIndex( productDme => {
      return dmeId === productDme
    })

    if ( itemInRemoved === -1 ) return setRemovedItems( [ ...removedItems, dmeId ] )
    else {
      const updatedRemovedItems = [ ...removedItems ]
      updatedRemovedItems.splice( itemInRemoved, 1 )
      setRemovedItems( [ ...updatedRemovedItems ] )
    }
  }

  const handleSuccessfulOrderEdit = ({
    deductible,
    deductible_remaining,
    monthly_max,
    total_charge,
    coinsurance_percentage,
    number_of_months,
    est_monthly_due,
    total_to_collect
  } : OrderEditData ) => {
    const updatedItems = snlInvoice?.details?.filter( product => {
      const productRemoved = removedItems.some( dmeId => product.dme_id === dmeId )
      return !productRemoved
    })
    // update invoice to use pricing from estimate
    setEditedInvoice( Object.assign({}, snlInvoice, {
      deductible,
      deductible_remaining,
      coinsurance_percentage,
      monthly_max,
      number_of_months,
      est_monthly_due,
      total_ins_payment: total_charge - total_to_collect,
      total_to_collect,
      total_patient_owes: total_to_collect,
      details: updatedItems
    }) )

    setIsEditing( false )
    setKeepOpen( true )

    document.getElementById( `edit-order` )?.scrollIntoView({
      behavior: `smooth`
    })
  }

  const handleGetEstimate = async ( batchId: string ) => {
    const defaultEditOrderError = `Oops... We are having trouble getting your estimate please try again or reach out to customer service if the issue persists.`
    const getEstimateResponse = await getEstimate( batchId )
      .catch( ( error: Error ) => {
        setEditOrderError( defaultEditOrderError )
        reportToSentry( `AOB Estimate Error: Exception getting estimate...`, {
          error,
          patientHash: snlInvoiceHash,
          invoice: snlInvoice?.invoice_number
        })
      })

    setLoading( false )

    if ( getEstimateResponse?.meta?.status !== `OK` || !getEstimateResponse?.data?.length ) return setEditOrderError( defaultEditOrderError )

    return handleSuccessfulOrderEdit( getEstimateResponse.data[0] )

  }

  const handlePostEstimate = async () => {
    const defaultEditOrderError = `Oops... We are having trouble getting your estimate please try again or reach out to customer service if the issue persists.`
    setEditOrderError( `` )

    setLoading( true )
    const includedItems = snlInvoice?.details?.filter( product => {
      const inRemovedItems = removedItems.some( dmeId => {
        return product.dme_id === dmeId
      })
      return !inRemovedItems
    })
      .map( product => {
        return {
          dme_id: product.dme_id,
          qty: product.quantity
        }
      })
    const postEstimateResponse = await postEstimate({
      patient_hash: snlInvoiceHash,
      items: includedItems
    })
      .catch( error => {
        setLoading( false )
        setEditOrderError( defaultEditOrderError )
        reportToSentry( `AOB Estimate Error: Exception posting estimate...`, {
          error,
          hash: snlInvoiceHash,
          invoice: snlInvoice?.invoice_number
        })
      })
    if ( postEstimateResponse?.meta?.status !== `OK` || !postEstimateResponse?.data?.batch_id ) {
      setLoading( false )

      return setEditOrderError( defaultEditOrderError )
    }

    return await handleGetEstimate( postEstimateResponse.data.batch_id )
  }

  if ( !snlInvoice || error || !snlInvoice?.details?.length ) return null

  return (
    <div className="bg-[#F3F5F7] rounded-lg flex flex-col gap-4 w-full" id="edit-order">

      <AccordionSection
        title="Order Summary:"
        amount={!isEditing ? <p className="a text-lg" onClick={() => { setIsEditing( true ); setKeepOpen( true ) }} data-testid="snl-edit-order-summary">
          {`Edit`}
        </p> : null}
        hideClose={isEditing}
        open={isEditing || keepOpen}
        setOpen={setKeepOpen}
        toggleOpenText="Show Details"
        toggleCloseText="Hide Details"
      >
        <div className="flex flex-col gap-4 py-3">
          {
            invoice?.details?.map( product => {
              const isItemRemoved = removedItems?.some( dmeId => {
                return dmeId === product.dme_id
              })

              if ( !isEditing && isItemRemoved ) return null

              return (
                <div
                  key={product.dme_id} className="cursor-pointer flex gap-3"
                  data-testid="snl-order-remove-item"
                >
                  { isEditing && <SfCheckbox checked={!isItemRemoved} onChange={() => removeItem( product.dme_id )} />}
                  <div className="flex flex-col gap-2">
                    <p className="font-semibold">{product.item_description}</p>
                    <p className="text-sm text-darkGray">{`Quantity ${product.quantity}`}</p>
                    <p className="font-light text-sm">{`What Insurance Pays: $${product.ins_payment?.toFixed( 2 )}`}</p>
                    <p className="font-light text-sm">{`What You Owe: $${product.patient_owes?.toFixed( 2 )}`}</p>
                  </div>

                </div>
              )
            })
          }
        </div>
      </AccordionSection>


      {
        isEditing && (
          <div className="flex flex-col items-center justify-center mx-auto gap-1 mb-3 w-80">
            <button
              className="btn btn-primary" disabled={loading} onClick={async () => await handlePostEstimate()}
              data-testid="snl-post-estimate-button"
            >
              {
                loading ? (
                  <div className="flex gap-2 cursor-pointer items-center justify-center mx-auto">
                    <LoadingSpinner height={`1rem`} width={`1rem`} rings={3} />
                    {
                      `Updating`
                    }
                  </div>
                ) : `Update`
              }
            </button>
            <button
              disabled={loading} className="btn btn-tertiary" onClick={() => setIsEditing( false )}
              data-testid="snl-edit-cancel-button"
            >
              {`Cancel`}
            </button>
            {
              editOrderError && <p className="text-center text-error">{editOrderError}</p>
            }
          </div>
        )
      }
    </div>
  )
}

export default EditInvoice